import React, { useState, useEffect } from 'react';
import { Form, Checkbox, Upload, Card, Switch, Space, message, Row, Col, Tooltip, Spin, Typography } from 'antd';
import { InboxOutlined, UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { DIGITAL_PRODUCTS } from '../../models/digitalProducts';
import { SUBSCRIPTION_TIERS } from '../../models/subscription';

const { Dragger } = Upload;
const { Title } = Typography;

export default function TemplatesAndDocuments({ form }) {
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingUploads, setPendingUploads] = useState([]);

  // Watch for subscription plan changes
  useEffect(() => {
    const subscriptionPlan = form.getFieldValue('subscriptionPlan');
    if (subscriptionPlan === 'enterprise-pro') {
      // For Enterprise Pro, select all digital products
      const allProductIds = Object.values(DIGITAL_PRODUCTS).map(p => p.id);
      form.setFieldsValue({ digitalProducts: allProductIds });
    }
  }, [form.getFieldValue('subscriptionPlan')]);

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    const allProductIds = Object.values(DIGITAL_PRODUCTS).map(p => p.id);
    setSelectedTemplates(checked ? allProductIds : []);
    form.setFieldsValue({ digitalProducts: checked ? allProductIds : [] });
  };

  // Function to process pending uploads once we have an organization ID
  const processPendingUploads = async (organizationId) => {
    if (pendingUploads.length === 0) return;

    setUploading(true);
    try {
      for (const file of pendingUploads) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        
        await new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              const response = await fetch('/api/upload', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  file: reader.result,
                  organizationId,
                  fileName: file.name
                }),
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Upload failed');
              }

              const data = await response.json();
              message.success(`${file.name} uploaded successfully`);
              resolve();
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
        });
      }
      setPendingUploads([]);
    } catch (error) {
      console.error('Error processing pending uploads:', error);
      message.error('Some files failed to upload. They will be retried when you complete the setup.');
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const organizationId = form.getFieldValue('organizationId');
        
        if (!organizationId) {
          // Store the file for later upload
          setPendingUploads(prev => [...prev, file]);
          message.info('File will be uploaded after organization setup is complete');
          onSuccess({ status: 'pending' });
          return;
        }

        setUploading(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        
        reader.onload = async () => {
          try {
            const response = await fetch('/api/upload', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                file: reader.result,
                organizationId,
                fileName: file.name
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || 'Upload failed');
            }

            const data = await response.json();
            onSuccess(data);
          } catch (error) {
            console.error('Upload error:', error);
            onError(error);
          } finally {
            setUploading(false);
          }
        };
        
        reader.onerror = () => {
          setUploading(false);
          onError(new Error('Failed to read file'));
        };
      } catch (error) {
        setUploading(false);
        onError(error);
      }
    },
    onChange(info) {
      const { status } = info.file;
      if (status === 'done') {
        if (info.file.response?.status === 'pending') {
          message.info(`${info.file.name} will be uploaded after organization setup is complete`);
        } else {
          message.success(`${info.file.name} file uploaded successfully.`);
        }
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed: ${info.file.error?.message || 'Unknown error'}`);
      }
    },
    beforeUpload(file) {
      const isPDF = file.type === 'application/pdf';
      const isWord = file.type === 'application/msword' || 
                    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isExcel = file.type === 'application/vnd.ms-excel' ||
                     file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const isPowerPoint = file.type === 'application/vnd.ms-powerpoint' ||
                          file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
      const isText = file.type === 'text/plain';
      
      if (!isPDF && !isWord && !isExcel && !isPowerPoint && !isCSV && !isText) {
        message.error('You can only upload PDF, Word, Excel, PowerPoint, CSV, or text documents!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('File must be smaller than 10MB!');
        return false;
      }
      return true;
    },
  };

  return (
    <div>
      <Card 
        title={
          <span>
            Digital Products
            <Tooltip title="Select which digital products this organization should have access to">
              <QuestionCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </span>
        } 
        className="mb-4"
      >
        <Form.Item
          name="digitalProducts"
          rules={[{ required: true, message: 'Please select at least one digital product' }]}
        >
          <Checkbox.Group
            value={selectedTemplates}
            onChange={(checkedValues) => {
              setSelectedTemplates(checkedValues);
              setSelectAll(checkedValues.length === Object.keys(DIGITAL_PRODUCTS).length);
              form.setFieldsValue({ digitalProducts: checkedValues });
            }}
            style={{ width: '100%' }}
          >
            <div>
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAll}
                style={{ marginBottom: 16 }}
              >
                Select All
              </Checkbox>
              <div style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                {Object.values(DIGITAL_PRODUCTS).map(product => (
                  <Col span={12} key={product.id}>
                    <Checkbox value={product.id}>
                      <div>{product.name}</div>
                      <div className="text-muted" style={{ fontSize: '0.8em', marginLeft: '24px' }}>
                        {product.description}
                      </div>
                    </Checkbox>
                  </Col>
                ))}
                              </Row>
                </div>
            </div>
          </Checkbox.Group>
        </Form.Item>
      </Card>

      <Card 
        title={
          <span>
            Upload Client Documents
            <Tooltip title="Upload any client documents to be saved to the organisation profile">
              <QuestionCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </span>
        } 
        className="mb-4"
      >
        <Form.Item
          name="clientDocuments"
          valuePropName="fileList"
          getValueFromEvent={e => {
            if (Array.isArray(e)) {
              return e;
            }
            return e?.fileList;
          }}
        >
          <Dragger {...uploadProps} disabled={uploading}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {uploading ? 'Uploading...' : 'Click or drag files to this area to upload'}
            </p>
            <p className="ant-upload-hint">
              Support for PDF, Word, Excel, PowerPoint, CSV, and text documents up to 10MB
              {pendingUploads.length > 0 && (
                <span style={{ display: 'block', marginTop: 8, color: '#1890ff' }}>
                  {pendingUploads.length} file(s) will be uploaded when setup completes
                </span>
              )}
            </p>
          </Dragger>
        </Form.Item>
      </Card>

      <Card title="AI Features">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form.Item
            name="aiSnapshotEnabled"
            label="Enable AI Snapshot Generation"
            valuePropName="checked"
            tooltip="AI will automatically generate incident snapshots"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="aiPirEnabled"
            label="Enable AI-Generated PIR"
            valuePropName="checked"
            tooltip="AI will help generate Preliminary Incident Reports"
          >
            <Switch />
          </Form.Item>


        </Space>
      </Card>
    </div>
  );
} 